import { LOAN_CHIP_AMOUNT, LOAN_PENALTY, MAX_LOANS, STARTING_CHIPS } from './types';

export interface ChipBreakdown {
  tens: number;
  fives: number;
  ones: number;
}

/** Convert total points into 10/5/1 chip breakdown (greedy) */
export function breakdownChips(total: number): ChipBreakdown {
  const safe = Math.max(0, total);
  const tens = Math.floor(safe / 10);
  const remainder = safe % 10;
  const fives = Math.floor(remainder / 5);
  const ones = remainder % 5;
  return { tens, fives, ones };
}

export function sumBreakdown(breakdown: ChipBreakdown): number {
  return breakdown.tens * 10 + breakdown.fives * 5 + breakdown.ones;
}

export function canAfford(amount: number, chips: number): boolean {
  return chips >= amount && amount >= 0;
}

export function validateBet(amount: number, chips: number): boolean {
  return Number.isInteger(amount) && amount >= 1 && amount <= chips;
}

/** Mission swap cost equals current draft turn (1-3) */
export function missionSwapCost(draftTurn: number): number {
  if (draftTurn < 1 || draftTurn > 3) return 0;
  return draftTurn;
}

export function canSwapMission(
  betAmount: number,
  draftTurn: number,
): boolean {
  const cost = missionSwapCost(draftTurn);
  return draftTurn >= 1 && draftTurn <= 3 && betAmount - cost >= 1;
}

/** Success payout: betAmount * payout multiplier from house */
export function calculateMissionPayout(betAmount: number, payoutMultiplier: number): number {
  return betAmount * payoutMultiplier;
}

export function applyMissionSuccess(
  playerChips: number,
  betAmount: number,
  payoutMultiplier: number,
  houseChips: number,
): { playerChips: number; houseChips: number; payout: number } {
  const payout = calculateMissionPayout(betAmount, payoutMultiplier);
  const newHouse = houseChips + betAmount - payout;
  return {
    playerChips: playerChips + payout,
    houseChips: newHouse,
    payout,
  };
}

export function applyMissionFailure(
  playerChips: number,
  betAmount: number,
  houseChips: number,
): { playerChips: number; houseChips: number } {
  return {
    playerChips,
    houseChips: houseChips + betAmount,
  };
}

export function applySwapCost(
  betAmount: number,
  houseChips: number,
  draftTurn: number,
): { betAmount: number; houseChips: number } | null {
  const cost = missionSwapCost(draftTurn);
  if (!canSwapMission(betAmount, draftTurn)) return null;
  return {
    betAmount: betAmount - cost,
    houseChips: houseChips + cost,
  };
}

export function canTakeLoan(loanTokens: number): boolean {
  return loanTokens < MAX_LOANS;
}

export function applyLoan(
  playerChips: number,
  loanTokens: number,
  houseChips: number,
): { playerChips: number; loanTokens: number; houseChips: number } | null {
  if (!canTakeLoan(loanTokens) || houseChips < LOAN_CHIP_AMOUNT) return null;
  return {
    playerChips: playerChips + LOAN_CHIP_AMOUNT,
    loanTokens: loanTokens + 1,
    houseChips: houseChips - LOAN_CHIP_AMOUNT,
  };
}

export function calculateFinalScore(
  chips: number,
  unusedMissionPayoutSum: number,
  loanTokens: number,
): number {
  return chips - unusedMissionPayoutSum - loanTokens * LOAN_PENALTY;
}

export function deductBet(playerChips: number, betAmount: number): number {
  return playerChips - betAmount;
}

export { STARTING_CHIPS, LOAN_CHIP_AMOUNT, LOAN_PENALTY, MAX_LOANS };
